
import React, { useRef } from 'react';
import { Card } from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';
import CameraQrScanner from './CameraQrScanner';
import { useQrScannerState } from './hooks';
import { ScannerCardHeader, ScannerCardContent } from './components';

interface QrScannerProps {
  onQrScan: (data: string) => void;
  onCodeSubmit: (code: string) => void;
  onRefreshData?: () => void;
}

const QrScanner = ({ onQrScan, onCodeSubmit, onRefreshData }: QrScannerProps) => {
  const isMobile = useIsMobile();
  const {
    showCamera,
    isRefreshing,
    cameraKey,
    openCamera,
    handleQrScan,
    closeCamera,
    openCameraExternally
  } = useQrScannerState({ onQrScan, onRefreshData });
  
  // Create a ref to expose methods to parent components
  const qrScannerRef = useRef({
    openCamera: openCameraExternally
  });
  
  // Expose the qrScannerRef to parent components
  React.useImperativeHandle(qrScannerRef, () => ({
    openCamera: openCameraExternally
  }));
  
  if (showCamera) {
    return (
      <CameraQrScanner 
        key={cameraKey}
        onQrDetected={handleQrScan}
        onClose={closeCamera}
      />
    );
  }
  
  return (
    <Card className="overflow-hidden border-0 shadow-md bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30" data-qr-scanner="true">
      <ScannerCardHeader 
        onRefreshData={onRefreshData} 
        isRefreshing={isRefreshing} 
      />
      
      <ScannerCardContent 
        openCamera={openCamera} 
        onCodeSubmit={onCodeSubmit} 
        isMobile={isMobile} 
      />
    </Card>
  );
};

// Create a forwardRef version that can be called from parent
const QrScannerWithRef = React.forwardRef<{openCamera: () => void}, QrScannerProps>((props, ref) => {
  const component = <QrScanner {...props} />;
  return component;
});

QrScannerWithRef.displayName = 'QrScannerWithRef';

export default React.memo(QrScanner);
